import { useEffect, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import TabsPanel from '../../components/TabsPanel';
import Loader from '../../components/Loader';
import { setCasesAccountTabs } from '../../constants/tabRoutes';
import { getCase } from '../../api/casesApi/casesApi';
import { useHasPermission } from '../../hooks/useHasPermission';
import { CaseContext } from './CaseContext';

const CaseAccountView = () => {
  const { caseId } = useParams();
  const [entityRecordData, setEntityRecordData] = useState<{ [key: string]: unknown }>();

  const fetchCaseData = useCallback(() => {
    if (!caseId) return;

    getCase(Number(caseId)).then((res) => {
      const plaintiff = res.data.client;

      const defendants = res.data.defendants.map((defendant) => ({
        defendantId: { id: defendant.id, label: defendant.name },
        gross: defendant.grossSettlement?.toString() || '',
      }));

      const mappedSecondaryFirms = res.data?.secondaryFirms?.map((firm) => ({
        firmId: {
          id: firm.id.toString(),
          label: firm.name,
        },
        firmRole: firm.role,
      }));

      setEntityRecordData({
        id: res.data.id,
        referenceId: res.data.referenceId,
        caseTypeCategory: res.data.type.category,
        type: {
          id: res.data.type.id,
          label: res.data.type.name,
        },
        trustAccount: {
          id: res.data?.trustAccount?.id.toString() || '',
          label: res.data?.trustAccount?.name || '',
        },
        clientId: { id: plaintiff.id, label: `${plaintiff.firstName} ${plaintiff.lastName}` },
        defendant: defendants,
        sol: res.data.sol ? moment(res.data.sol) : null,
        filedAt: res.data.filedAt ? moment(res.data.filedAt) : null,
        name: res.data.name,
        secondaryFirms: mappedSecondaryFirms,
        status: res.data.status,
        isLeadFirm: res.data.isLeadFirm,
        canBeAbandoned: res.data.canBeAbandoned,
        paymentStarted: res.data.paymentStarted,
      });
    });
  }, [caseId]);

  useEffect(() => {
    fetchCaseData();
  }, [fetchCaseData]);

  const canViewPayments = useHasPermission('Payments', 'View');
  const isCaseLoaded = !!entityRecordData;
  const isDraft = entityRecordData && entityRecordData.status === 'Draft';
  const isLeadFirm = entityRecordData?.isLeadFirm !== false;

  // Hidden tabs: Document requests, Data requests, Signature requests
  const hiddenTabPaths = ['document-requests', 'data-requests', 'signature-requests'];
  // Deductions, Settlements and Drafts are lead-firm-only areas.
  const leadFirmOnlyTabPaths = ['deductions', 'settlements', 'drafts', 'ledger'];
  const allTabs = setCasesAccountTabs(!!isDraft);
  // Tabs depend on isLeadFirm, which is only known once the case has loaded.
  const filteredTabs = isCaseLoaded
    ? allTabs.filter((tab) => {
        if (hiddenTabPaths.includes(tab.path)) return false;
        if (!isLeadFirm && leadFirmOnlyTabPaths.includes(tab.path)) return false;
        if ((tab.path === 'deductions' || tab.path === 'settlements') && !isDraft && !canViewPayments) return false;
        return true;
      })
    : [];

  return (
    <CaseContext.Provider value={{ refetchCase: fetchCaseData }}>
      <Box display="flex" width="100%">
        {isCaseLoaded ? (
          <TabsPanel
            tabsList={filteredTabs}
            locationSlicePosition={3}
            contextData={entityRecordData}
          />
        ) : (
          <Loader colorType="warning" />
        )}
      </Box>
    </CaseContext.Provider>
  );
};

export default CaseAccountView;
